/**
 * Updates the recently viewed products in localStorage.
 *
 * Storage format (v2): Array<{ id: string, ts: number }>, most-recent first.
 * Legacy format (v1) was Array<string>; it is migrated transparently on read.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store (PRD: last 15) */
  static #MAX_PRODUCTS = 15;
  /** @static @constant {number} Time-to-live in ms (PRD: 30 days) */
  static #TTL_MS = 30 * 24 * 60 * 60 * 1000;

  /**
   * Reads raw entries, migrating the legacy string[] format and pruning
   * anything older than the TTL. Always returns newest-first.
   * @returns {{ id: string, ts: number }[]}
   */
  static getEntries() {
    /** @type {unknown} */
    let raw;
    try {
      raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      raw = [];
    }
    if (!Array.isArray(raw)) return [];

    const now = Date.now();
    const entries = raw
      .map((/** @type {any} */ item) =>
        typeof item === 'string' ? { id: item, ts: now } : item
      )
      .filter(
        (/** @type {any} */ e) =>
          e && typeof e.id === 'string' && typeof e.ts === 'number' && now - e.ts < this.#TTL_MS
      );

    return entries.slice(0, this.#MAX_PRODUCTS);
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string|number} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const id = String(productId);
    if (!id || id === 'null' || id === 'undefined') return;

    let entries = this.getEntries().filter((e) => e.id !== id);
    entries.unshift({ id, ts: Date.now() });
    entries = entries.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(entries));
    } catch {
      /* storage full / disabled — ignore */
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed product IDs (newest-first).
   * Kept returning string[] for backwards compatibility (predictive search).
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProducts() {
    return this.getEntries().map((e) => e.id);
  }
}
